import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clock, ChevronUp, ChevronDown, X } from 'lucide-react';

const TimePicker = ({
  value = '',
  onChange = () => {},
  placeholder = 'Select time',
  disabled = false,
  required = false,
  label = '',
  error = '',
  className = '',
  size = 'md',
  format = '24', // '12' or '24'
  step = 15, // minutes step
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timePickerRef = useRef(null);

  // Helper functions - defined before useState to avoid hoisting issues
  const parseTime = useCallback((timeString) => {
    if (!timeString) return { hours: 0, minutes: 0, period: 'AM' };
    
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    if (format === '12') {
      return {
        hours: period === 'PM' && hours !== 12 ? hours + 12 : period === 'AM' && hours === 12 ? 0 : hours,
        minutes: minutes || 0,
        period: period || 'AM'
      };
    } else {
      return {
        hours: hours || 0,
        minutes: minutes || 0,
        period: hours >= 12 ? 'PM' : 'AM'
      };
    }
  }, [format]);

  const formatTime = (time) => {
    if (!time) return '';
    
    if (format === '12') {
      const displayHours = time.hours === 0 ? 12 : time.hours > 12 ? time.hours - 12 : time.hours;
      const displayMinutes = time.minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes} ${time.period}`;
    } else {
      const displayHours = time.hours.toString().padStart(2, '0');
      const displayMinutes = time.minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes}`;
    }
  };

  const formatDisplayTime = (time) => {
    if (!time || (time.hours === 0 && time.minutes === 0)) return placeholder;
    return formatTime(time);
  };

  // Initialize state with parsed value
  const [selectedTime, setSelectedTime] = useState(value ? parseTime(value) : { hours: 0, minutes: 0, period: 'AM' });

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const baseClasses = 'block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-white cursor-pointer';
  
  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected time when value prop changes
  useEffect(() => {
    if (value) {
      setSelectedTime(parseTime(value));
    } else {
      setSelectedTime({ hours: 0, minutes: 0, period: 'AM' });
    }
  }, [value, parseTime]);

  const generateTimeOptions = () => {
    const options = [];
    const totalMinutes = 24 * 60;
    
    for (let minutes = 0; minutes < totalMinutes; minutes += step) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      
      if (format === '12') {
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const period = hours >= 12 ? 'PM' : 'AM';
        const timeString = `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
        options.push({
          value: `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`,
          label: timeString,
          hours,
          minutes,
          period
        });
      } else {
        const timeString = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
        options.push({
          value: timeString,
          label: timeString,
          hours,
          minutes,
          period: hours >= 12 ? 'PM' : 'AM'
        });
      }
    }
    
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleTimeSelect = (option) => {
    setSelectedTime({
      hours: option.hours,
      minutes: option.minutes,
      period: option.period
    });
    onChange(option.value);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedTime({ hours: 0, minutes: 0, period: 'AM' });
    onChange('');
  };

  const incrementHours = () => {
    const newHours = (selectedTime.hours + 1) % 24;
    const newTime = { ...selectedTime, hours: newHours };
    setSelectedTime(newTime);
    onChange(`${newHours.toString().padStart(2, '0')}:${selectedTime.minutes.toString().padStart(2, '0')}`);
  };

  const decrementHours = () => {
    const newHours = selectedTime.hours === 0 ? 23 : selectedTime.hours - 1;
    const newTime = { ...selectedTime, hours: newHours };
    setSelectedTime(newTime);
    onChange(`${newHours.toString().padStart(2, '0')}:${selectedTime.minutes.toString().padStart(2, '0')}`);
  };

  const incrementMinutes = () => {
    const newMinutes = (selectedTime.minutes + step) % 60;
    const newTime = { ...selectedTime, minutes: newMinutes };
    setSelectedTime(newTime);
    onChange(`${selectedTime.hours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`);
  };

  const decrementMinutes = () => {
    const newMinutes = selectedTime.minutes === 0 ? 60 - step : selectedTime.minutes - step;
    const newTime = { ...selectedTime, minutes: newMinutes };
    setSelectedTime(newTime);
    onChange(`${selectedTime.hours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`);
  };

  const togglePeriod = () => {
    if (format === '12') {
      const newPeriod = selectedTime.period === 'AM' ? 'PM' : 'AM';
      const newTime = { ...selectedTime, period: newPeriod };
      setSelectedTime(newTime);
      const displayHours = selectedTime.hours === 0 ? 12 : selectedTime.hours > 12 ? selectedTime.hours - 12 : selectedTime.hours;
      onChange(`${displayHours}:${selectedTime.minutes.toString().padStart(2, '0')} ${newPeriod}`);
    }
  };

  const isTimeSelected = (option) => {
    return option.hours === selectedTime.hours && 
           option.minutes === selectedTime.minutes && 
           option.period === selectedTime.period;
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative" ref={timePickerRef}>
        <div
          className={inputClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          {...props}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Clock className="w-4 h-4 text-gray-400 mr-2" />
              <span className={selectedTime && (selectedTime.hours !== 0 || selectedTime.minutes !== 0) ? 'text-gray-900' : 'text-gray-400'}>
                {formatDisplayTime(selectedTime)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {selectedTime && (selectedTime.hours !== 0 || selectedTime.minutes !== 0) && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="p-4">
              {/* Time Display and Controls */}
              <div className="flex items-center justify-center space-x-4 mb-4">
                {/* Hours */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {format === '12' 
                      ? (selectedTime.hours === 0 ? 12 : selectedTime.hours > 12 ? selectedTime.hours - 12 : selectedTime.hours)
                      : selectedTime.hours.toString().padStart(2, '0')
                    }
                  </div>
                  <div className="flex flex-col space-y-1">
                    <button
                      type="button"
                      onClick={incrementHours}
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <ChevronUp className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      type="button"
                      onClick={decrementHours}
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <ChevronDown className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>

                <div className="text-2xl font-bold text-gray-400">:</div>

                {/* Minutes */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedTime.minutes.toString().padStart(2, '0')}
                  </div>
                  <div className="flex flex-col space-y-1">
                    <button
                      type="button"
                      onClick={incrementMinutes}
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <ChevronUp className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      type="button"
                      onClick={decrementMinutes}
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <ChevronDown className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* AM/PM Toggle (12-hour format only) */}
                {format === '12' && (
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 mb-2">
                      {selectedTime.period}
                    </div>
                    <button
                      type="button"
                      onClick={togglePeriod}
                      className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200 transition-colors"
                    >
                      Toggle
                    </button>
                  </div>
                )}
              </div>

              {/* Quick Time Options */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Quick Select</div>
                <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {timeOptions.filter((_, index) => index % 4 === 0).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleTimeSelect(option)}
                      className={`px-3 py-2 text-sm rounded-md transition-colors ${
                        isTimeSelected(option)
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default TimePicker;
